/**
 * @page DailyOpeningPage
 * @description 每日開帳頁（UI-4-1）
 * 前日結轉 + 備用金 → 開帳金額確認
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { safeFormatDate } from '../../lib/format';
import { api } from '../../lib/api';
import type { DailySessionDto } from '@pokerroom/types';

export default function DailyOpeningPage() {
  const { t } = useTranslation('accounting');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [pettyCash, setPettyCash] = useState('');

  const { data: activeSession } = useQuery<DailySessionDto | null>({
    queryKey: ['accounting-active-session'],
    queryFn: () => api.get<DailySessionDto | null>('/accounting/sessions/active'),
  });

  const { data: dayBasis } = useQuery<{ accountingBusinessDate: string; zipaBusinessDate: string }>(
    { queryKey: ['accounting-business-day'], queryFn: () => api.get('/accounting/business-day') },
  );

  const openMutation = useMutation({
    mutationFn: (dto: { pettyCash: string }) => api.post('/accounting/sessions/open', dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting-active-session'] });
      navigate('/accounting/expenses');
    },
    onError: (err) =>
      console.warn('[DailyOpening] open failed:', err instanceof Error ? err.message : err),
  });

  if (activeSession) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center">
        <div className="text-4xl mb-3">✅</div>
        <h2 className="text-xl font-bold text-base-content mb-2">{t('alreadyOpened')}</h2>
        <p className="text-base-content/60 mb-1">
          {t('openedTime', {
            time: safeFormatDate(activeSession.openedAt, (d) => d.toLocaleString()),
          })}
        </p>
        <p className="text-base-content/60 mb-6">
          {t('openedAmount')}
          <span className="font-bold text-base-content">
            NT${parseFloat(activeSession.openingBalance).toLocaleString()}
          </span>
        </p>
        <button
          onClick={() => navigate('/accounting/expenses')}
          className="px-6 py-2 rounded-md bg-primary text-primary-content"
        >
          {t('goToExpenses')}
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-lg mx-auto">
      <h1 className="text-2xl font-bold text-base-content mb-6">{t('openSession')}</h1>

      <div className="bg-base-200 border border-base-300 rounded-xl p-6 space-y-5">
        {/* 今日日期 */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-base-content/60">{t('sessionDate')}</span>
          <span className="font-medium text-base-content">
            {dayBasis?.accountingBusinessDate ?? '-'}
          </span>
        </div>

        {/* 前日結轉（自動帶入） */}
        <div className="flex justify-between items-center">
          <span className="text-sm text-base-content/60">{t('previousBalance')}</span>
          <span className="font-medium text-base-content">{t('autoCarryForward')}</span>
        </div>

        {/* 備用金輸入 */}
        <div>
          <label className="block text-sm text-base-content/70 mb-1.5">
            {t('pettyCash')} (NT$) *
          </label>
          <input
            type="number"
            min="0"
            step="100"
            value={pettyCash}
            onChange={(e) => setPettyCash(e.target.value)}
            placeholder="5000"
            className="w-full px-3 py-2.5 rounded-md border border-base-300 bg-base-200 text-base-content
                       focus:outline-hidden focus:ring-2 focus:ring-primary text-lg font-mono"
          />
        </div>

        {/* 開帳金額預覽 */}
        {pettyCash && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <p className="text-sm text-base-content/60 mb-1">
              {t('openingBalance')}（{t('estimated')}）
            </p>
            <p className="text-2xl font-bold text-primary">
              NT${parseFloat(pettyCash || '0').toLocaleString()}
            </p>
            <p className="text-xs text-base-content/40 mt-1">{t('balanceFormula')}</p>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 rounded-md border border-base-300 text-sm"
        >
          {tc('cancel')}
        </button>
        <button
          onClick={() => openMutation.mutate({ pettyCash })}
          disabled={!pettyCash || parseFloat(pettyCash) < 0 || openMutation.isPending}
          className="px-6 py-2 rounded-md bg-primary text-primary-content text-sm disabled:opacity-50 font-medium"
        >
          {openMutation.isPending ? tc('loading') : t('confirmOpen')}
        </button>
      </div>
    </div>
  );
}
